/**
 * AD-2653: the school's own set-up, sent to Felicity with each question, so she answers for this school.
 * Only names and settings are sent: the School Setup settings, the filter groups the school has hidden, and
 * the access the person and parents have to each profile section. No names of people, no email
 * addresses, no logins. A new school may not have set everything up yet.
 */
const GENERAL_SETTINGS = [
    ['hideBoarderGroups', 'Hide Boarder Groups'],
    ['hideHouseGroups', 'Hide House Groups'],
    ['disableEnquiriesSync', 'Disable Enquiries Synchronization'],
    ['disableApplicationParentInvitations', 'Disable Application Parent Invitations'],
    ['disableStudentInvitations', 'Disable Student Invitations'],
    ['disableEmployeeInvitations', 'Disable Employee Invitations'],
    ['disableParentNotificationApplicationUpdates', 'Disable Parent Notifications for Application Profile Updates'],
    ['disableParentNotificationStudentUpdates', 'Disable Parent Notifications for Student Profile Updates'],
    ['mfaEnabled', 'Require email verification code on login']
];

const NUMBER_SETTINGS = [
    // the names on the School Setup tab (Maria, 28 October 2026: there is no General Settings any more)
    ['noAccountAutoReminderInterval', 'Auto Reminders, Account Creation (hours)'],
    ['unconfirmedApplicationReminderIntervalInHours', 'Auto Reminders, Applicant Profile (hours)'],
    ['unconfirmedStudentReminderIntervalInHours', 'Auto Reminders, Student Profile (hours)'],
    ['medicineExpiryWarningDays', 'Auto Reminders, Medicine Expiry (days)']
];

const SETUP_LIMIT = 4000;

/** "Permission.StudentMedicalConditions.Edit" and the like, as "StudentMedicalConditions: Edit". */
export const accessOf = (permissions = []) => {
    const access = {};
    permissions.forEach(permission => {
        const [, area, level] = String(permission).split('.');
        if (!area || !/^(View|Edit)$/.test(level ?? '')) {
            return;
        }
        access[area] = access[area] === 'Edit' || level === 'Edit' ? 'Edit' : 'View';
    });
    return Object.entries(access).sort(([a], [b]) => a.localeCompare(b)).map(([area, level]) => `${area}: ${level}`);
};

/** The names of the filter groups the school has hidden, wherever they sit in the tree. */
export const hiddenGroupsOf = (groups) => {
    const hidden = [];
    const walk = (node) => {
        if (Array.isArray(node)) {
            node.forEach(walk);
            return;
        }
        if (!node || typeof node !== 'object') {
            return;
        }
        if (node.isVisible === false && node.name) {
            hidden.push(node.name);
        }
        Object.values(node).forEach(value => typeof value === 'object' && walk(value));
    };
    walk(groups);
    return [...new Set(hidden)];
};

export const describeSetup = ({school, permissions, parentPermissions, groups} = {}) => {
    const parts = [];
    if (school) {
        const ticked = GENERAL_SETTINGS.map(([key, label]) => `${label}: ${school[key] ? 'on' : 'off'}`);
        const numbers = NUMBER_SETTINGS
            .filter(([key]) => school[key] !== undefined && school[key] !== null)
            .map(([key, label]) => `${label}: ${school[key]}`);
        parts.push(`School Setup tab of Settings: ${[...ticked, ...numbers].join('; ')}`);
    }
    const hidden = hiddenGroupsOf(groups);
    if (groups) {
        parts.push(`Filter groups hidden by the school: ${hidden.length ? hidden.join(', ') : 'none'}`);
    }
    if (permissions?.length) {
        parts.push(`This person's access to profile sections: ${accessOf(permissions).join(', ')}`);
    }
    if (parentPermissions?.length) {
        parts.push(`Parents' access to profile sections: ${accessOf(parentPermissions).join(', ')}`);
    }
    return parts.join('\n').slice(0, SETUP_LIMIT);
};
